<script setup lang="ts">
    import { computed } from 'vue';
    import { Chart } from 'vue-chartjs';
    import { Chart as ChartJS, BarController, BarElement, CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
    import type { ChartData, TooltipItem } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import type { InsightsMonthlyTrend } from '../insights-dummy-data';
    import { chartColor } from '@shared/modules/chart-colors';
    import { formatCompact, formatMonth, formatPercent, rootFontSize } from '../insights-theme';

    ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip, Legend);
    applyChartDefaults();

    const props = defineProps<{
        trend: InsightsMonthlyTrend[];
    }>();

    const RATE_SERIES = [
        { key: 'OpenPercentage', label: 'Open rate', color: 'eventOpened', decimals: 1 },
        { key: 'ClickPercentage', label: 'Click rate', color: 'eventClicked', decimals: 1 },
        { key: 'BouncePercentage', label: 'Bounce rate', color: 'eventBounced', decimals: 2 },
        { key: 'UnsubscribePercentage', label: 'Unsubscribe rate', color: 'eventUnsubscribed', decimals: 2 },
    ] as const;

    const SENT_LABEL = 'Emails sent';

    const chartData = computed<ChartData<'bar' | 'line'>>(() => ({
        labels: props.trend.map((m) => formatMonth(m.Month)),
        datasets: [
            ...RATE_SERIES.map((s) => ({
                type: 'line' as const,
                label: s.label,
                data: props.trend.map((m) => m[s.key]),
                yAxisID: 'y',
                order: 0,
                borderColor: chartColor(s.color),
                backgroundColor: chartColor(s.color),
                borderWidth: 2,
                tension: 0.3,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBorderWidth: 2,
                pointHoverBorderColor: chartColor('surface'),
            })),
            {
                type: 'bar' as const,
                label: SENT_LABEL,
                data: props.trend.map((m) => m.SentCount),
                yAxisID: 'y1',
                order: 1,
                backgroundColor: chartColor('eventSent', 0.3),
                hoverBackgroundColor: chartColor('eventSent', 0.45),
                borderRadius: 4,
                maxBarThickness: 32,
            },
        ],
    }));

    function formatTooltipValue(item: TooltipItem<'bar' | 'line'>): string {
        if (item.dataset.label === SENT_LABEL) return formatCompact(item.parsed.y);
        const series = RATE_SERIES.find((s) => s.label === item.dataset.label);
        return formatPercent(item.parsed.y, series?.decimals);
    }

    const chartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
            legend: {
                position: 'top' as const,
                align: 'end' as const,
                labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle', font: { size: rootFontSize('--font-size-small') } },
            },
            tooltip: {
                displayColors: true,
                callbacks: {
                    title: (items: TooltipItem<'bar' | 'line'>[]) => formatMonth(props.trend[items[0].dataIndex].Month, 'long'),
                    label: (item: TooltipItem<'bar' | 'line'>) => ` ${item.dataset.label}: ${formatTooltipValue(item)}`,
                },
            },
        },
        scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { font: { size: rootFontSize('--font-size-small') } } },
            y: {
                position: 'left' as const,
                beginAtZero: true,
                border: { display: false },
                grid: { color: chartColor('grid') },
                ticks: { font: { size: rootFontSize('--font-size-small') }, maxTicksLimit: 5, callback: (value: string | number) => `${value}%` },
            },
            y1: {
                position: 'right' as const,
                beginAtZero: true,
                border: { display: false },
                grid: { display: false },
                ticks: { font: { size: rootFontSize('--font-size-small') }, maxTicksLimit: 5, callback: (value: string | number) => formatCompact(Number(value)) },
            },
        },
    }));
</script>

<template>
    <div class="insights-chart p-relative">
        <Chart type="bar" :data="chartData" :options="chartOptions" aria-label="Emails sent, open, click, bounce and unsubscribe rate per month" role="img" />
    </div>
</template>
